import { useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { SignInError, type SignInFailure } from '../auth/session';
import { WineBottleLogo } from '../components/WineBottleLogo';
import { passwordProblems } from '../domain/password';
import { PasswordInput } from '../components/PasswordInput';
import { t, type MessageKey } from '../i18n';
import './SignInPage.css';

type Step = 'signIn' | 'newPassword' | 'forgot' | 'reset';

const FAILURE_MESSAGE: Record<SignInFailure, MessageKey> = {
  refused: 'signIn.refused',
  invalidPassword: 'newPassword.invalid',
  invalidCode: 'reset.invalidCode',
  tooManyAttempts: 'signIn.tooManyAttempts',
  network: 'signIn.network',
};

const failureOf = (error: unknown): MessageKey =>
  FAILURE_MESSAGE[error instanceof SignInError ? error.reason : 'network'];

/**
 * /connexion (FR-024, FR-028, FR-045, contracts/ui-and-emails.md). With the real sign-in (T074a):
 * email and password; an invited person's first sign-in asks them to choose their own password;
 * a forgotten password is replaced with a code sent by email. A wrong password, an unknown address
 * and a stopped person all get the same message. On previews the fake sign-in asks for an email
 * only. Look: owner request of 2026-09-28 (bottle logo, red-wine background, large button).
 */
export function SignInPage() {
  const { session } = useAuth();
  const [step, setStep] = useState<Step>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<MessageKey | undefined>();
  const [notice, setNotice] = useState<MessageKey | undefined>();
  const [busy, setBusy] = useState(false);

  /** Runs one sign-in step, showing its failure message if it fails. */
  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await action();
    } catch (caught) {
      setError(failureOf(caught));
    } finally {
      setBusy(false);
    }
  }

  function go(next: Step, message?: MessageKey) {
    setStep(next);
    setError(undefined);
    setNotice(message);
    setNewPassword('');
    setConfirmation('');
    setCode('');
  }

  const problems = passwordProblems(newPassword);
  const newPasswordReady = problems.length === 0 && newPassword === confirmation;

  function handleSignIn(event: FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    void run(async () => {
      const result = await session.signIn(email, password);
      if (result.kind === 'newPasswordRequired') go('newPassword');
    });
  }

  function handleNewPassword(event: FormEvent) {
    event.preventDefault();
    if (!newPasswordReady) return;
    void run(async () => {
      await session.completeNewPassword(newPassword);
    });
  }

  function handleForgot(event: FormEvent) {
    event.preventDefault();
    void run(async () => {
      await session.startPasswordReset(email);
      go('reset', 'reset.codeSent');
    });
  }

  function handleReset(event: FormEvent) {
    event.preventDefault();
    if (!newPasswordReady) return;
    void run(async () => {
      await session.finishPasswordReset(email, code, newPassword);
      setPassword('');
      go('signIn', 'reset.done');
    });
  }

  const emailField = (
    <>
      <label htmlFor="email">{t('signIn.emailLabel')}</label>
      <input
        id="email"
        type="email"
        required
        autoComplete="username"
        placeholder={t('signIn.emailPlaceholder')}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
    </>
  );

  /** The new password twice, with the FR-045 rules checked as the person types. */
  const newPasswordFields = (
    <>
      <p className="sign-in__rules">{t('password.rules')}</p>
      <label htmlFor="new-password">{t('newPassword.label')}</label>
      <PasswordInput
        id="new-password"
        autoComplete="new-password"
        value={newPassword}
        onChange={(event) => setNewPassword(event.target.value)}
      />
      {newPassword !== '' && problems.length > 0 && (
        <ul className="sign-in__problems">
          {problems.map((problem) => (
            <li key={problem}>{t(`password.${problem}`)}</li>
          ))}
        </ul>
      )}
      <label htmlFor="confirm-password">{t('newPassword.confirm')}</label>
      <PasswordInput
        id="confirm-password"
        autoComplete="new-password"
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
      />
      {confirmation !== '' && confirmation !== newPassword && (
        <p className="sign-in__problems">{t('newPassword.mismatch')}</p>
      )}
    </>
  );

  let content: ReactNode;
  if (step === 'newPassword') {
    content = (
      <>
        <h1 className="sign-in__welcome">{t('newPassword.title')}</h1>
        <p>{t('newPassword.intro')}</p>
        <form className="sign-in__form" onSubmit={handleNewPassword}>
          {newPasswordFields}
          <button type="submit" className="sign-in__submit" disabled={busy || !newPasswordReady}>
            {t('newPassword.submit')}
          </button>
        </form>
      </>
    );
  } else if (step === 'forgot' || step === 'reset') {
    content = (
      <>
        <h1 className="sign-in__welcome">{t('reset.title')}</h1>
        {step === 'forgot' ? (
          <form className="sign-in__form" onSubmit={handleForgot}>
            <p>{t('reset.intro')}</p>
            {emailField}
            <button type="submit" className="sign-in__submit" disabled={busy}>
              {t('reset.send')}
            </button>
          </form>
        ) : (
          <form className="sign-in__form" onSubmit={handleReset}>
            <label htmlFor="reset-code">{t('reset.codeLabel')}</label>
            <input
              id="reset-code"
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            {newPasswordFields}
            <button type="submit" className="sign-in__submit" disabled={busy || !newPasswordReady}>
              {t('reset.submit')}
            </button>
          </form>
        )}
        <button type="button" className="sign-in__link" onClick={() => go('signIn')}>
          {t('reset.back')}
        </button>
      </>
    );
  } else {
    content = (
      <>
        {/* The welcome line is the page's heading; no separate "Connexion" title (owner, 2026-09-28). */}
        <h1 className="sign-in__welcome">{t('signIn.welcome')}</h1>
        <form className="sign-in__form" onSubmit={handleSignIn}>
          {emailField}
          {session.usesPasswords && (
            <>
              <label htmlFor="password">{t('signIn.passwordLabel')}</label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </>
          )}
          <button type="submit" className="sign-in__submit" disabled={busy}>
            {t('signIn.submit')}
          </button>
        </form>
        {session.usesPasswords && (
          <button type="button" className="sign-in__link" onClick={() => go('forgot')}>
            {t('signIn.forgot')}
          </button>
        )}
      </>
    );
  }

  return (
    <div className="sign-in">
      <main className="sign-in__panel">
        <WineBottleLogo className="sign-in__logo" />
        {content}
        {notice && (
          <p role="status" className="sign-in__notice">
            {t(notice)}
          </p>
        )}
        {error && (
          <p role="alert" className="sign-in__error">
            {t(error)}
          </p>
        )}
      </main>
    </div>
  );
}
